import React, { useCallback, useEffect, useRef } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { RoomEvent, type RoomEventHandlerMap } from 'matrix-js-sdk';
import { useMatrixClient } from '../../../hooks/useMatrixClient';
import { useElevoConfig } from '../../../hooks/useElevoConfig';
import { todosAtom } from '../../../state/todos/todosAtom';
import { fetchTodosPage, type TodoItem } from './useTodosApi';
import { parseAskUser, parseToolCall } from '../../../components/message';

function TodosApiSync() {
  const mx = useMatrixClient();
  const setTodos = useSetAtom(todosAtom);
  const { todos } = useElevoConfig();
  const apiUrl = todos?.api;

  useEffect(() => {
    if (!apiUrl) return;
    let cancelled = false;

    const fetchFirstPage = async () => {
      setTodos({ type: 'SET_FETCHING', isFetching: true });
      setTodos({ type: 'SET_ERROR', error: null });
      try {
        const data = await fetchTodosPage(mx, apiUrl);
        if (!cancelled) {
          setTodos({
            type: 'SET_API_PAGE',
            items: data.todos,
            nextCursor: data.next_cursor,
            append: false,
          });
          setTodos({ type: 'SET_INITIALIZED' });
        }
      } catch (err) {
        if (!cancelled) {
          setTodos({
            type: 'SET_ERROR',
            error: err instanceof Error ? err : new Error(String(err)),
          });
        }
      } finally {
        if (!cancelled) {
          setTodos({ type: 'SET_FETCHING', isFetching: false });
        }
      }
    };

    fetchFirstPage();
    return () => {
      cancelled = true;
    };
  }, [mx, apiUrl, setTodos]);

  useEffect(
    () => () => {
      setTodos({ type: 'RESET' });
    },
    [setTodos],
  );

  return null;
}

export function useFetchTodosNextPage() {
  const mx = useMatrixClient();
  const setTodos = useSetAtom(todosAtom);
  const todosState = useAtomValue(todosAtom);
  const { todos } = useElevoConfig();
  const apiUrl = todos?.api;
  const fetchingRef = useRef(false);

  return useCallback(async () => {
    if (!apiUrl || !todosState.nextCursor || fetchingRef.current || todosState.isFetching) return;
    fetchingRef.current = true;
    setTodos({ type: 'SET_FETCHING', isFetching: true });
    try {
      const data = await fetchTodosPage(mx, apiUrl, todosState.nextCursor);
      setTodos({
        type: 'SET_API_PAGE',
        items: data.todos,
        nextCursor: data.next_cursor,
        append: true,
      });
    } catch (err) {
      console.error('Failed to fetch todos next page:', err);
    } finally {
      fetchingRef.current = false;
      setTodos({ type: 'SET_FETCHING', isFetching: false });
    }
  }, [mx, apiUrl, todosState.nextCursor, todosState.isFetching, setTodos]);
}

function TodosTimelineSync() {
  const mx = useMatrixClient();
  const setTodos = useSetAtom(todosAtom);

  useEffect(() => {
    const handleTimelineEvent: RoomEventHandlerMap[RoomEvent.Timeline] = (
      mEvent,
      room,
      _toStart,
      _removed,
      data,
    ) => {
      if (!data.liveEvent || !room) return;

      // Handle redaction
      if (mEvent.isRedaction()) {
        const redactedEventId = mEvent.event.redacts;
        if (redactedEventId) {
          setTodos({ type: 'REMOVE_BY_EVENT', roomId: room.roomId, eventId: redactedEventId });
        }
        return;
      }

      if (mEvent.getType() !== 'm.room.message') return;

      const content = mEvent.getContent();
      if (!content) return;

      const relation = mEvent.getRelation();
      const isReplace = relation?.rel_type === 'm.replace';
      const renderContent = isReplace ? content['m.new_content'] : content;
      const messageContent =
        renderContent && typeof renderContent === 'object'
          ? (renderContent as Record<string, unknown>)
          : content;

      const askUser = parseAskUser(messageContent);
      if (askUser?.answers) {
        const eventId = isReplace ? relation.event_id : mEvent.getId();
        if (eventId) {
          setTodos({ type: 'REMOVE_BY_QUESTION_ID', questionId: eventId });
        }
        return;
      }

      const toolCall = parseToolCall(messageContent);

      if (toolCall) {
        const eventId = mEvent.getId();

        // Skip m.replace events for adding (the original event already handled it)
        if (isReplace) return;

        // Add todo when ask-user question is pending for current user
        if (askUser && eventId) {
          const assignee =
            typeof content['vip.elevo.initial_human_sender'] === 'string'
              ? content['vip.elevo.initial_human_sender']
              : undefined;
          if (assignee && assignee === mx.getUserId()) {
            const sender = mEvent.getSender();
            if (sender) {
              const todoItem: TodoItem = {
                room_id: room.roomId,
                question_event_id: eventId,
                thread_root_id: mEvent.threadRootId,
                sender,
                assignee,
                question: askUser,
                created_at: Math.floor(mEvent.getTs() / 1000),
              };
              setTodos({ type: 'ADD_LIVE_ITEM', item: todoItem });
            }
          }
        }
      }
    };

    mx.on(RoomEvent.Timeline, handleTimelineEvent);
    return () => {
      mx.removeListener(RoomEvent.Timeline, handleTimelineEvent);
    };
  }, [mx, setTodos]);

  return null;
}

export function TodosSyncFeature() {
  return (
    <>
      <TodosApiSync />
      <TodosTimelineSync />
    </>
  );
}
